import React, { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import { formatMoney } from '../utils/money';
import ProductImport, { getImportText } from './ProductImport';
import CatalogHealth from '../components/CatalogHealth';

const COST_TEXT = {
  es: {
    zeroSales: '{{count}} venta(s) guardadas con coste 0',
    needCost: 'Pon un coste para poder corregirlas.',
    apply: 'Aplicar {{cost}} a esas ventas',
    confirm: '\u00bfAplicar el coste {{cost}} a {{count}} venta(s) anteriores que se guardaron con coste 0? Las ventas que ya tienen coste no se tocan.',
    applied: '{{count}} venta(s) corregidas con el coste {{cost}}.',
    applyError: 'No se pudo aplicar el coste.',
    history: 'Historial',
    hideHistory: 'Ocultar',
    historyTitle: 'Historial de coste',
    historyEmpty: 'Todav\u00eda no hay cambios de coste registrados para este producto.',
    historyLoading: 'Cargando...',
    historyError: 'No se pudo cargar el historial.',
    sources: { manual: 'Manual', import: 'Importador', shopify: 'Shopify', desconocido: 'Sin origen' },
    colDate: 'Fecha',
    colBefore: 'Antes',
    colAfter: 'Despu\u00e9s',
    colSource: 'Origen',
    costHint: 'Las ventas anteriores mantienen su coste: el nuevo se aplica a las ventas futuras.',
  },
  en: {
    zeroSales: '{{count}} sale(s) saved with cost 0',
    needCost: 'Set a cost to be able to fix them.',
    apply: 'Apply {{cost}} to those sales',
    confirm: 'Apply the cost {{cost}} to {{count}} past sale(s) saved with cost 0? Sales that already have a cost are not changed.',
    applied: '{{count}} sale(s) fixed with cost {{cost}}.',
    applyError: 'Could not apply the cost.',
    history: 'History',
    hideHistory: 'Hide',
    historyTitle: 'Cost history',
    historyEmpty: 'No cost changes recorded for this product yet.',
    historyLoading: 'Loading...',
    historyError: 'Could not load the history.',
    sources: { manual: 'Manual', import: 'Importer', shopify: 'Shopify', desconocido: 'Unknown' },
    colDate: 'Date',
    colBefore: 'Before',
    colAfter: 'After',
    colSource: 'Source',
    costHint: 'Past sales keep their cost: the new one applies to future sales.',
  },
  fr: {
    zeroSales: '{{count}} vente(s) enregistr\u00e9es avec un co\u00fbt de 0',
    needCost: 'Indiquez un co\u00fbt pour pouvoir les corriger.',
    apply: 'Appliquer {{cost}} \u00e0 ces ventes',
    confirm: 'Appliquer le co\u00fbt {{cost}} \u00e0 {{count}} vente(s) pass\u00e9es enregistr\u00e9es avec un co\u00fbt de 0 ? Les ventes qui ont d\u00e9j\u00e0 un co\u00fbt ne sont pas modifi\u00e9es.',
    applied: '{{count}} vente(s) corrig\u00e9es avec le co\u00fbt {{cost}}.',
    applyError: 'Impossible d\u2019appliquer le co\u00fbt.',
    history: 'Historique',
    hideHistory: 'Masquer',
    historyTitle: 'Historique du co\u00fbt',
    historyEmpty: 'Aucun changement de co\u00fbt enregistr\u00e9 pour ce produit pour l\u2019instant.',
    historyLoading: 'Chargement...',
    historyError: 'Impossible de charger l\u2019historique.',
    sources: { manual: 'Manuel', import: 'Import', shopify: 'Shopify', desconocido: 'Inconnue' },
    colDate: 'Date',
    colBefore: 'Avant',
    colAfter: 'Apr\u00e8s',
    colSource: 'Origine',
    costHint: 'Les ventes pass\u00e9es gardent leur co\u00fbt : le nouveau s\u2019applique aux ventes futures.',
  },
};

function Products({ token, onAuthError, tenant }) {
  const isAdmin = !!(tenant && tenant.role === 'admin');
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProduct, setNewProduct] = useState({
    sku: '', name: '', cogs: '0', sale_price: '', current_stock: '0', min_stock_alert: '0',
  });
  const [creating, setCreating] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [notice, setNotice] = useState('');
  const [applyingId, setApplyingId] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [historyRows, setHistoryRows] = useState(null);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => { fetchProducts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/products`, authHeaders);
      setProducts(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
      } else {
        setError(t('products.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/products`,
        {
          ...newProduct,
          cogs: parseFloat(newProduct.cogs || 0),
          sale_price: newProduct.sale_price ? parseFloat(newProduct.sale_price) : null,
          current_stock: parseInt(newProduct.current_stock || 0, 10),
          min_stock_alert: parseInt(newProduct.min_stock_alert || 0, 10),
        },
        authHeaders
      );
      setProducts((prev) => [...prev, response.data.data]);
      setShowCreateForm(false);
      setNewProduct({ sku: '', name: '', cogs: '0', sale_price: '', current_stock: '0', min_stock_alert: '0' });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('products.createError'));
    } finally {
      setCreating(false);
    }
  };

  const startEditing = (product) => {
    setEditingId(product.id);
    setEditValues({
      cogs: product.cogs ?? 0,
      sale_price: product.sale_price ?? '',
      current_stock: product.track_stock === false ? '' : (product.current_stock ?? 0),
      min_stock_alert: product.min_stock_alert ?? 0,
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEditing = async (productId) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      // Los campos vacios no se envian (el servidor espera numeros, no texto vacio).
      const payload = {};
      const toNumber = (v, parse) => (v === '' || v === null || v === undefined ? undefined : parse(String(v).replace(',', '.')));
      const cogs = toNumber(editValues.cogs, parseFloat);
      const salePrice = toNumber(editValues.sale_price, parseFloat);
      const stock = toNumber(editValues.current_stock, (x) => parseInt(x, 10));
      const minAlert = toNumber(editValues.min_stock_alert, (x) => parseInt(x, 10));
      if (Number.isFinite(cogs)) payload.cogs = cogs;
      if (Number.isFinite(salePrice)) payload.sale_price = salePrice;
      if (Number.isFinite(stock)) payload.current_stock = stock;
      if (Number.isFinite(minAlert)) payload.min_stock_alert = minAlert;
      const response = await axios.patch(
        `${API_URL}/api/v1/products/${productId}`,
        payload,
        authHeaders
      );
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, ...response.data.data } : p))
      );
      setEditingId(null);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
      } else {
        setError(t('products.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (productId) => {
    const confirmMsg = isAdmin ? t('products.deleteConfirm') : t('products.deleteRequestConfirm');
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await axios.delete(`${API_URL}/api/v1/products/${productId}`, authHeaders);
      if (isAdmin) {
        setProducts((prev) => prev.filter((p) => p.id !== productId));
      } else {
        window.alert(response.data.message || t('products.requestSent'));
      }
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) setError(err.response.data.detail);
      else setError(t('products.deleteError'));
    }
  };

  const inputClass =
    'bg-surfaceAlt border border-edge rounded px-2 py-1 text-white text-sm w-20 focus:outline-none focus:border-accent';

  const incompleteCount = products.filter((p) => p.sale_price == null).length;
  const money = (value) => formatMoney(value, i18n.language, tenant && tenant.default_currency);
  const ct = COST_TEXT[(i18n.language || 'es').slice(0, 2)] || COST_TEXT.es;

  const toggleHistory = async (product) => {
    if (historyId === product.id) {
      setHistoryId(null);
      return;
    }
    setHistoryId(product.id);
    setHistoryRows(null);
    setHistoryError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/products/${product.id}/cost-history`, authHeaders);
      setHistoryRows(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setHistoryError(ct.historyError);
    }
  };

  const formatDate = (value) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

  const applyCostToZeroSales = async (product) => {
    const cost = money(product.cogs);
    const count = product.zero_cost_sales;
    if (!window.confirm(ct.confirm.replace('{{cost}}', cost).replace('{{count}}', count))) return;
    setApplyingId(product.id);
    setError('');
    setNotice('');
    try {
      const response = await axios.post(`${API_URL}/api/v1/products/${product.id}/apply-cost-to-zero-sales`, {}, authHeaders);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, zero_cost_sales: 0 } : p)));
      setNotice(ct.applied.replace('{{count}}', response.data.updated).replace('{{cost}}', cost));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else {
        const detail = err.response && err.response.data && err.response.data.detail;
        setError(typeof detail === 'string' ? detail : ct.applyError);
      }
    } finally {
      setApplyingId(null);
    }
  };

  const columns = [
    t('products.colSku'), t('products.colName'), t('products.colCogs'), t('products.colSalePrice'), t('products.colMargin'),
    t('products.colStock'), t('products.colMinAlert'), '',
  ];

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-white text-2xl font-bold">{t('products.title')}</h2>
          <p className="text-muted text-sm mt-1">{t('products.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
        <button
          onClick={() => setShowImport((v) => !v)}
          className="bg-surfaceAlt border border-edge text-white font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
        >
          {showImport ? t('products.cancel') : getImportText(i18n.language).openButton}
        </button>
        <button
          onClick={() => setShowCreateForm((v) => !v)}
          className="self-start sm:self-auto bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
        >
          {showCreateForm ? t('products.cancel') : t('products.addProduct')}
        </button>
        </div>
      </div>

      {showImport && (
        <ProductImport
          token={token}
          onAuthError={onAuthError}
          onClose={() => setShowImport(false)}
          onImported={fetchProducts}
        />
      )}

      <CatalogHealth products={products} />

      {incompleteCount > 0 && (
        <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-4 mb-6">
          <p className="text-amber-400 text-sm">{t('products.incompleteProducts', { count: incompleteCount })}</p>
        </div>
      )}

      {showCreateForm && (
        <form onSubmit={createProduct} className="bg-surface border border-edge rounded-2xl p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colSku')}</label>
              <input
                type="text"
                required
                value={newProduct.sku}
                onChange={(e) => setNewProduct((v) => ({ ...v, sku: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colName')}</label>
              <input
                type="text"
                required
                value={newProduct.name}
                onChange={(e) => setNewProduct((v) => ({ ...v, name: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colCogs')}</label>
              <input
                type="number"
                step="0.01"
                value={newProduct.cogs}
                onChange={(e) => setNewProduct((v) => ({ ...v, cogs: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colSalePrice')}</label>
              <input
                type="number"
                step="0.01"
                value={newProduct.sale_price}
                onChange={(e) => setNewProduct((v) => ({ ...v, sale_price: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colStock')}</label>
              <input
                type="number"
                value={newProduct.current_stock}
                onChange={(e) => setNewProduct((v) => ({ ...v, current_stock: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('products.colMinAlert')}</label>
              <input
                type="number"
                value={newProduct.min_stock_alert}
                onChange={(e) => setNewProduct((v) => ({ ...v, min_stock_alert: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer"
              >
                {creating ? '...' : t('products.createProduct')}
              </button>
            </div>
          </div>
        </form>
      )}

      {notice && (
        <div className="bg-surfaceAlt border border-accent text-accent text-sm rounded-lg p-4 mb-6">
          {notice}
        </div>
      )}

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6">
        {loading ? (
          <p className="text-muted text-center py-12">{t('products.loading')}</p>
        ) : products.length === 0 ? (
          <p className="text-muted text-center py-12">{t('products.noProductsYet')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-edge">
                  {columns.map((h, idx) => (
                    <th key={idx} className="text-muted text-xs text-left pb-3 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const isEditing = editingId === product.id;
                  const untracked = product.track_stock === false;
                  const lowStock = !untracked && product.current_stock <= (product.min_stock_alert ?? 0);
                  return (
                    <Fragment key={product.id}>
                    <tr className="border-b border-surfaceAlt">
                      <td className="py-4 text-[#888] text-[13px] font-mono">{product.sku}</td>
                      <td className="py-4 text-white text-sm">{product.name}</td>
                      <td className="py-4 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            className={inputClass}
                            value={editValues.cogs}
                            onChange={(e) => setEditValues((v) => ({ ...v, cogs: e.target.value }))}
                          />
                        ) : (
                          <span className="text-muted">{money(product.cogs)}</span>
                        )}
                        {isEditing && <p className="text-muted text-[11px] mt-1 max-w-[180px]">{ct.costHint}</p>}
                        {!isEditing && product.zero_cost_sales > 0 && (
                          <div className="mt-1 max-w-[200px]">
                            <p className="text-amber-400 text-[11px]">{ct.zeroSales.replace('{{count}}', product.zero_cost_sales)}</p>
                            {Number(product.cogs) > 0 ? (
                              isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => applyCostToZeroSales(product)}
                                  disabled={applyingId === product.id}
                                  className="text-accent text-[11px] underline cursor-pointer text-left"
                                >
                                  {ct.apply.replace('{{cost}}', money(product.cogs))}
                                </button>
                              )
                            ) : (
                              <p className="text-muted text-[11px]">{ct.needCost}</p>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-4 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            className={inputClass}
                            value={editValues.sale_price}
                            onChange={(e) => setEditValues((v) => ({ ...v, sale_price: e.target.value }))}
                          />
                        ) : (
                          <span className="text-white">{product.sale_price != null ? money(product.sale_price) : '\u2014'}</span>
                        )}
                      </td>
                      <td className="py-4 text-sm">
                        {product.sale_price != null ? (
                          (() => {
                            const margin = product.sale_price - (product.cogs ?? 0);
                            const marginPct = product.sale_price > 0 ? ((margin / product.sale_price) * 100).toFixed(0) : null;
                            return (
                              <span className={margin >= 0 ? 'text-accent' : 'text-red-400'}>
                                {money(margin)} {marginPct !== null && `(${marginPct}%)`}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-muted">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="py-4 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            className={inputClass}
                            value={editValues.current_stock}
                            placeholder={untracked ? t('products.notTracked') : undefined}
                            onChange={(e) => setEditValues((v) => ({ ...v, current_stock: e.target.value }))}
                          />
                        ) : untracked ? (
                          <span className="text-muted text-xs" title={t('products.notTrackedHint')}>
                            {t('products.notTracked')}
                          </span>
                        ) : (
                          <span className={lowStock ? 'text-amber-500 font-semibold' : 'text-white'}>
                            {product.current_stock}
                          </span>
                        )}
                      </td>
                      <td className="py-4 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            className={inputClass}
                            value={editValues.min_stock_alert}
                            onChange={(e) => setEditValues((v) => ({ ...v, min_stock_alert: e.target.value }))}
                          />
                        ) : (
                          <span className="text-muted">{product.min_stock_alert}</span>
                        )}
                      </td>
                      <td className="py-4">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEditing(product.id)}
                              disabled={saving}
                              className="bg-accent text-panel text-xs font-bold px-3 py-1.5 rounded cursor-pointer"
                            >
                              {saving ? '...' : t('products.save')}
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="bg-transparent border border-edge text-muted text-xs px-3 py-1.5 rounded cursor-pointer"
                            >
                              {t('products.cancel')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditing(product)}
                            className="bg-surfaceAlt border border-edge text-white text-xs px-3 py-1.5 rounded cursor-pointer"
                          >
                            {t('products.edit')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleHistory(product)}
                          className="bg-transparent border border-edge text-muted text-xs px-3 py-1.5 rounded cursor-pointer ml-2"
                        >
                          {historyId === product.id ? ct.hideHistory : ct.history}
                        </button>
                        <button
                          onClick={() => deleteProduct(product.id)}
                          className="text-red-400 text-xs px-3 py-1.5 rounded border border-[#7f1d1d] cursor-pointer ml-2"
                        >
                          {isAdmin ? t('products.delete') : t('products.requestDelete')}
                        </button>
                      </td>
                    </tr>
                    {historyId === product.id && (
                      <tr className="border-b border-surfaceAlt">
                        <td colSpan={columns.length} className="pb-4">
                          <div className="bg-surfaceAlt border border-edge rounded-lg p-4">
                            <p className="text-white text-sm font-semibold mb-3">{ct.historyTitle}</p>
                            {historyError ? (
                              <p className="text-red-400 text-xs">{historyError}</p>
                            ) : historyRows === null ? (
                              <p className="text-muted text-xs">{ct.historyLoading}</p>
                            ) : historyRows.length === 0 ? (
                              <p className="text-muted text-xs">{ct.historyEmpty}</p>
                            ) : (
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr>
                                    {[ct.colDate, ct.colBefore, ct.colAfter, ct.colSource].map((h) => (
                                      <th key={h} className="text-muted text-[11px] text-left pb-2 uppercase">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {historyRows.map((h, idx) => (
                                    <tr key={idx}>
                                      <td className="py-1.5 text-muted text-xs">{formatDate(h.changed_at)}</td>
                                      <td className="py-1.5 text-muted text-xs">{h.old_cost == null ? '\u2014' : money(h.old_cost)}</td>
                                      <td className="py-1.5 text-white text-xs">{money(h.new_cost)}</td>
                                      <td className="py-1.5 text-muted text-xs">{ct.sources[h.source] || h.source}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Products;